/*
 * @Description: 微应用菜单和路由注册中心 - 动态管理应用菜单配置和路由配置
 */

import { IMenuItem } from '@/layouts/components/SideMenu';
import version from '../../public/echo.json';
import {
  COMMON_FUNCTION_ROUTER_URL,
  payRoutes,
  reportRoutes,
  saleRoutes,
  stockRoutes,
} from '@monorepo/shared';
import Client, { IClientConfig } from 'mars-front-api-sdk/types/Client';
import { makeClient as BaseMakeClient } from 'mars-front-api-sdk';

/**
 * 路由存储结构：{ [appName: string]: any[] }
 */
type RoutesMap = Record<string, any[]>;

/**
 * 菜单和路由注册中心
 * 用于管理微应用动态注册的菜单配置和路由配置
 *
 * 设计理念：
 * - 所有菜单统一管理，不区分应用
 * - 通过版本时间戳判断是否需要更新
 * - 始终保持最新版本的完整菜单配置
 * - 统一管理路由，避免window全局污染问题
 */
class RegistryAPI {
  // 当前菜单构建时间
  private currentBuildTime: string = version?.data?.build_time;

  // 当前菜单配置
  private menus: Partial<Record<TTopMenuType, IMenuItem[]>> = {};
  // 菜单变化监听器
  private listeners: Set<(menus: Partial<Record<TTopMenuType, IMenuItem[]>>) => void> = new Set();

  private commonFunctionMenus: Record<string, string> = COMMON_FUNCTION_ROUTER_URL;
  private commonFunctionListeners: Set<(menus: Record<string, string>) => void> = new Set();

  private makeClientListeners: Set<() => void> = new Set();
  // 路由相关属性
  private routesMap: RoutesMap = {};
  private routeListeners: Set<(routes: any[]) => void> = new Set();
  private readonly defaultRoutesMap: RoutesMap = {
    'report-app': reportRoutes,
    'stock-app': stockRoutes,
    'sale-app': saleRoutes,
    'pay-app': payRoutes,
  };

  private registeredApps: Set<string> = new Set();

  constructor() {
    // 初始化为空，等待设置默认菜单
    // 初始化时使用默认路由
    this.routesMap = { ...this.defaultRoutesMap };
  }

  /**
   * 注册完整菜单配置（子应用更新时调用）
   * 处理的问题：base-app可能长时间不会更新，
   * 参考预发环境，只有子应用会更新，base-app不会更新，
   * 但是子应用会更新，所以需要判断子应用的构建时间是否更新，
   * 如果更新了，则更新base-app的菜单配置。
   * @param menus 完整的菜单配置数组（包含所有菜单项）
   * @param buildTime 子应用的构建时间戳（毫秒）
   *
   * @description
   * - 只有当 buildTime 比当前构建时间新时才会更新
   * - 传入的是完整菜单，会直接替换现有菜单
   * - 建议使用子应用的构建时间或发布时间作为 buildTime
   */
  registerMicro({
    menusFunction,
    appName,
    commonFunctionMenus,
    buildTime,
    appRoutes,
    makeClient,
  }: {
    appName: string;
    menusFunction: () => Partial<Record<string, any>>;
    commonFunctionMenus: Record<string, string>;
    buildTime: string;
    appRoutes: any[];
    makeClient: (config: IClientConfig) => Client;
  }): void {
    // 本地开发环境不做处理
    if (window.MARS_BASE_CONFIG.REACT_APP_ENV === 'proxy') return;

    // 检查应用是否已注册，避免重复注册
    if (this.registeredApps.has(appName)) {
      // console.log('[RegistryAPI] 应用已注册，忽略注册');
      return;
    }

    this.routesMap[appName] = appRoutes;
    this.notifyRouteListeners();
    this.registeredApps.add(appName);

    // 检查构建时间是否更新
    const isBuildTimeUpdated =
      !buildTime ||
      (this.currentBuildTime &&
        new Date(buildTime).getTime() <= new Date(this.currentBuildTime).getTime());

    if (isBuildTimeUpdated) {
      // 子应用构建时间晚于最新版本构建时间，忽略注册
      console.log('[RegistryAPI] 菜单构建时间未更新，忽略注册');
      console.log(
        `[RegistryAPI] - 当前构建时间: ${new Date(this.currentBuildTime).toLocaleString()}`,
      );
      console.log(`[RegistryAPI] - 传入构建时间: ${new Date(buildTime).toLocaleString()}`);
      return;
    }

    const menus = menusFunction();

    if (!menus || typeof menus !== 'object') return;

    // 替换为新菜单
    this.menus = menus;
    this.commonFunctionMenus = commonFunctionMenus;
    this.currentBuildTime = buildTime;
    window.makeClient = makeClient || BaseMakeClient;

    // 通知所有监听器
    this.notifyListeners();
    this.notifyMakeClientListeners();
    this.notifyCommonFunctionListeners();
  }

  // ==================== makeClient-sdk相关方法 ====================

  /**
   * 订阅菜单变化
   * @param listener 监听器函数，接收菜单对象和构建时间
   * @returns 取消订阅的函数
   */
  subscribeMakeClient(listener: () => void): () => void {
    this.makeClientListeners.add(listener);

    // 返回取消订阅函数
    return () => {
      this.makeClientListeners.delete(listener);
    };
  }

  private notifyMakeClientListeners(): void {
    this.makeClientListeners.forEach((listener) => {
      listener();
    });
  }
  // ==================== 菜单注册相关方法 ====================

  /**
   * 获取当前菜单配置
   * @returns 当前菜单对象的副本
   */
  getMenus(): Partial<Record<TTopMenuType, IMenuItem[]>> {
    return { ...this.menus }; // 返回副本，避免外部修改
  }

  /**
   * 订阅菜单变化
   * @param listener 监听器函数，接收菜单对象和构建时间
   * @returns 取消订阅的函数
   */
  subscribe(listener: (menus: Partial<Record<TTopMenuType, IMenuItem[]>>) => void): () => void {
    this.listeners.add(listener);

    // 返回取消订阅函数
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * 通知所有监听器
   */
  private notifyListeners(): void {
    const menus = this.getMenus();
    this.listeners.forEach((listener) => {
      try {
        listener(menus);
      } catch (error) {
        console.error('[RegistryAPI] 菜单监听器执行出错:', error);
      }
    });
  }

  // ==================== homeConfig相关方法 ====================

  subscribeCommonFunctionMenus(listener: (menus: Record<string, string>) => void): () => void {
    this.commonFunctionListeners.add(listener);

    // 返回取消订阅函数
    return () => {
      this.commonFunctionListeners.delete(listener);
    };
  }

  getCommonFunctionMenus(): Record<string, string> {
    return { ...this.commonFunctionMenus }; // 返回副本，避免外部修改
  }

  private notifyCommonFunctionListeners(): void {
    const commonFunctionMenus = this.getCommonFunctionMenus();
    this.commonFunctionListeners.forEach((listener) => {
      listener(commonFunctionMenus);
    });
  }

  // ==================== 路由注册相关方法 ====================

  /**
   * 获取所有路由（扁平化数组）
   * @returns 所有应用的路由合并后的数组
   */
  getRoutes(): any[] {
    // 将所有应用的路由合并成一个数组
    const allRoutes = Object.values(this.routesMap).flat();
    return [...allRoutes]; // 返回副本，避免外部修改
  }

  /**
   * 订阅路由变化
   * @param listener 监听器函数
   * @returns 取消订阅的函数
   */
  subscribeRoutes(listener: (routes: any[]) => void): () => void {
    this.routeListeners.add(listener);

    // 返回取消订阅函数
    return () => {
      this.routeListeners.delete(listener);
    };
  }

  /**
   * 通知所有路由监听器
   */
  private notifyRouteListeners(): void {
    const routes = this.getRoutes();
    this.routeListeners.forEach((listener) => {
      try {
        listener(routes);
      } catch (error) {
        console.error('[RegistryAPI] 路由监听器执行出错:', error);
      }
    });
  }
}

// 创建单例实例
const registryMicroAPI = new RegistryAPI();

// 挂载到 window 对象，便于子应用访问
if (typeof window !== 'undefined') {
  (window as any).__MARS_REGISTRY_MICRO_API__ = registryMicroAPI;
}

export default registryMicroAPI;
export { RegistryAPI };
